import React from 'react';
import { Col, Row } from 'antd';
import MetricChart from './MetricChart';
import SpendChart from './SpendChart';
import WoPerformanceSection from './WoPerformanceSection';
import { CHART_METRIC } from './LifeCycleAnalyticsConstants';

type MetricChartProps = React.ComponentProps<typeof MetricChart>;
type SpendChartProps = React.ComponentProps<typeof SpendChart>;
type WoPerformanceProps = React.ComponentProps<typeof WoPerformanceSection>;

export type LifeCycleAnalyticsChartsAndPerformanceProps = {
	showCharts: boolean;
	metricChartPropsList: (MetricChartProps & { key: string })[];
	spendChartProps: SpendChartProps;
	woPerformanceProps: WoPerformanceProps;
};

const LifeCycleAnalyticsChartsAndPerformance = ({
	showCharts,
	metricChartPropsList,
	spendChartProps,
	woPerformanceProps,
}: LifeCycleAnalyticsChartsAndPerformanceProps) => {
	if (!showCharts) {
		return null;
	}

	const metricByKey = {};
	metricChartPropsList.forEach((props) => {
		metricByKey[props.key] = props;
	});

	const resolutionProps = metricByKey[CHART_METRIC.RESOLUTION_TIME];
	const approvalProps = metricByKey[CHART_METRIC.APPROVAL_TIME];
	const responseProps = metricByKey[CHART_METRIC.RESPONSE_TIME];
	const ftfrProps = metricByKey[CHART_METRIC.FIRST_TIME_FIX_RATE];

	return (
		<>
			{/* Row 2: Avg Approval Time + Avg Resolution Time (approval omitted when auto-approval is on) */}
			{(approvalProps || resolutionProps) && (
				<div className="my-3 print:!mx-0 print:!my-0 print:!w-full print:!break-inside-avoid">
					<Row
						gutter={12}
						className="lifeCycleAnalyticsChartsRow print:!min-h-0 print:!break-inside-avoid print:!overflow-visible"
					>
						{approvalProps && resolutionProps ? (
							<>
								<Col span={12} className="print:!min-w-0 print:!break-inside-avoid print:!pb-4">
									<MetricChart {...approvalProps} />
								</Col>
								<Col span={12} className="print:!min-w-0 print:!break-inside-avoid print:!pb-4">
									<MetricChart {...resolutionProps} />
								</Col>
							</>
						) : (
							<Col span={24} className="print:!min-w-0 print:!break-inside-avoid print:!pb-4">
								<MetricChart {...(approvalProps || resolutionProps)} />
							</Col>
						)}
					</Row>
				</div>
			)}

			{/* Row 3: Avg Response Time + First Time Fix Rate */}
			{(responseProps || ftfrProps) && (
				<div className="my-3 print:!mx-0 print:!my-0 print:!w-full print:!break-inside-avoid">
					<Row
						gutter={12}
						className="lifeCycleAnalyticsChartsRow print:!min-h-0 print:!break-inside-avoid print:!overflow-visible"
					>
						{responseProps && (
							<Col
								span={ftfrProps ? 12 : 24}
								className="print:!min-w-0 print:!break-inside-avoid print:!pb-4"
							>
								<MetricChart {...responseProps} />
							</Col>
						)}
						{ftfrProps && (
							<Col
								span={responseProps ? 12 : 24}
								className="print:!min-w-0 print:!break-inside-avoid print:!pb-4"
							>
								<MetricChart {...ftfrProps} />
							</Col>
						)}
					</Row>
				</div>
			)}

			{/* Row 1: Avg Resolution Time (full width) */}
			{/* Spend chart (full width) */}
			<div className="my-3 print:!mx-0 print:!my-0 print:!w-full print:break-before-page print:!break-inside-avoid">
				<Row
					gutter={12}
					className="lifeCycleAnalyticsChartsRow print:!min-h-0 print:!break-inside-avoid print:!overflow-visible"
				>
					<Col span={24} className="print:!min-w-0 print:!break-inside-avoid print:!pb-4">
						<SpendChart {...spendChartProps} />
					</Col>
				</Row>
			</div>

			{/* New page after charts so WO Performance starts on its own page. */}
			<div className="woPerformanceSectionWrapper my-3 print:!mx-0 print:!my-0 print:!w-full print:break-before-page">
				<Row gutter={12} className="print:!mx-0 print:!min-h-0 print:!overflow-visible">
					<Col span={24}>
						<WoPerformanceSection {...woPerformanceProps} />
					</Col>
				</Row>
			</div>
		</>
	);
};

export default LifeCycleAnalyticsChartsAndPerformance;
